import { useId, type ReactNode } from 'react';
import './Controls.css';

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Accessible name when the label is visual only. */
  ariaLabel?: string | undefined;
  hint?: string | undefined;
  className?: string | undefined;
}

/** Single choice as a row of buttons (radio group semantics, arrow keys move). */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
  scroll = false,
  size = 'm',
}: {
  label: string;
  value: T;
  options: readonly SegmentOption<T>[];
  onChange: (value: T) => void;
  scroll?: boolean;
  size?: 'm' | 'l';
}) {
  const move = (dir: 1 | -1) => {
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + dir + options.length) % options.length];
    if (next) onChange(next.value);
  };
  return (
    <div
      className={`segmented segmented--${size}${scroll ? ' segmented--scroll' : ''}`}
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          move(1);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={o.ariaLabel}
            title={o.hint}
            tabIndex={checked ? 0 : -1}
            className={`segmented__item${checked ? ' is-active' : ''}${o.className ? ` ${o.className}` : ''}`}
            onClick={() => onChange(o.value)}
            ref={(el) => {
              if (el && checked && document.activeElement?.parentElement === el.parentElement) el.focus();
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Toggle chip (aria-pressed), used for filters and multi-select facets. */
export function Chip({
  pressed,
  onToggle,
  children,
  className,
}: {
  pressed: boolean;
  onToggle: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button type="button" className={`chip${pressed ? ' is-on' : ''}${className ? ` ${className}` : ''}`} aria-pressed={pressed} onClick={onToggle}>
      {children}
    </button>
  );
}

/** Single-choice chip group with radio semantics, for category and status pickers in forms. */
export function ChoiceChips<T extends string>({
  label,
  value,
  options,
  onChange,
  error,
}: {
  label: string;
  value: T | null;
  options: readonly { value: T; label: ReactNode; className?: string }[];
  onChange: (value: T) => void;
  error?: string | null | undefined;
}) {
  const id = useId();
  return (
    <fieldset className="choice" aria-describedby={error ? `${id}-err` : undefined}>
      <legend className="field__label">{label}</legend>
      <div className="choice__list">
        {options.map((o) => (
          <label key={o.value} className={`chip chip--choice${value === o.value ? ' is-on' : ''}${o.className ? ` ${o.className}` : ''}`}>
            <input type="radio" name={id} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
            {o.label}
          </label>
        ))}
      </div>
      {error && (
        <p id={`${id}-err`} className="field__error">
          {error}
        </p>
      )}
    </fieldset>
  );
}

/** Multi-choice chip group with checkbox semantics (the damage types since 0021). `values` keep the options' order. */
export function MultiChoiceChips<T extends string>({
  label,
  values,
  options,
  onChange,
  error,
  hint,
}: {
  label: string;
  values: readonly T[];
  options: readonly { value: T; label: ReactNode; className?: string }[];
  onChange: (values: T[]) => void;
  error?: string | null | undefined;
  hint?: string | undefined;
}) {
  const id = useId();
  const toggle = (value: T) =>
    onChange(options.map((o) => o.value).filter((v) => (v === value ? !values.includes(v) : values.includes(v))));
  return (
    <fieldset className="choice" aria-describedby={error ? `${id}-err` : hint ? `${id}-hint` : undefined}>
      <legend className="field__label">{label}</legend>
      <div className="choice__list">
        {options.map((o) => {
          const on = values.includes(o.value);
          return (
            <label key={o.value} className={`chip chip--choice${on ? ' is-on' : ''}${o.className ? ` ${o.className}` : ''}`}>
              <input type="checkbox" name={id} value={o.value} checked={on} onChange={() => toggle(o.value)} />
              {o.label}
            </label>
          );
        })}
      </div>
      {error ? (
        <p id={`${id}-err`} className="field__error">
          {error}
        </p>
      ) : (
        hint && (
          <p id={`${id}-hint`} className="field__hint">
            {hint}
          </p>
        )
      )}
    </fieldset>
  );
}

/** On/off switch with a visible label and hint. */
export function Switch({
  checked,
  onChange,
  label,
  hint,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="switch-row">
      <div className="switch-row__text">
        <label htmlFor={id} className="field__label">
          {label}
        </label>
        {hint && <p className="field__hint">{hint}</p>}
      </div>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={`switch${checked ? ' is-on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <span className="switch__knob" />
      </button>
    </div>
  );
}
